<!--
   Created by Claude on 2024
   千人画师征集活动报名页面
-->
<template>
  <div class="recruitment">
    <!-- 活动说明 -->
    <div class="recruitment-section">
      <div class="section-title">活动说明</div>
      <div class="section-content">
        <ol class="rules-list">
          <li>请于临界平台发布近 60 天内创作的 4 个原创作品作为报名例图。</li>
          <li>
            收到中选通知后，须在规定时间内于临界发布 0.01r
            稿位抽选，例图须与报名时一致，并在表单中填写由平台支付的稿费金额。
          </li>
          <li>完成订单后，凭订单ID联系【临界福利官】登记发放稿费</li>
        </ol>
      </div>
    </div>

    <!-- 用户信息 -->
    <div class="recruitment-form">
      <div class="user-info" v-if="userInfo?.userId">
        <img class="user-avatar" :src="userInfo.avatarUrl" alt="" />
        <span class="user-name">{{ userInfo.displayName || "用户" }}</span>
      </div>

      <!-- 例图选择 -->
      <div class="form-group">
        <div class="form-label">例图</div>
        <div
          class="upload-area"
          v-if="selectedWorks.length === 0"
          @click="handleSelectWorks"
        >
          <van-icon name="plus" class="upload-icon" />
          <span class="upload-text">选择作品</span>
        </div>
        <div class="selected-works" v-else>
          <div
            class="work-item"
            v-for="work in selectedWorks"
            :key="work.id"
            @click="handleSelectWorks"
          >
            <img :src="work.cover + '!mfit_w200_h200_webp'" alt="" />
          </div>
        </div>
      </div>

      <!-- 稿费输入 -->
      <div class="form-group">
        <div class="form-label">需要平台支付的稿费</div>
        <div class="input-wrapper">
          <input
            type="number"
            class="fee-input"
            v-model="feeAmount"
            placeholder="请输入正整数"
            @input="handleFeeInput"
          />
        </div>
      </div>
    </div>

    <!-- 底部按钮 -->
    <div class="recruitment-footer">
      <van-button
        class="submit-btn"
        :class="{ disabled: !canSubmit }"
        block
        @click="handleSubmit"
        :loading="submitting"
      >
        提交
      </van-button>
    </div>

    <!-- 选择作品弹窗 -->
    <van-popup
      v-model:show="showWorksPicker"
      position="bottom"
      :style="{ height: '80%' }"
      round
    >
      <WorksPicker
        :works="worksList"
        :selected="selectedWorks"
        :max="4"
        @confirm="handleWorksConfirm"
        @close="showWorksPicker = false"
      />
    </van-popup>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import {
  Icon as VanIcon,
  Button as VanButton,
  Popup as VanPopup,
  showToast,
  showSuccessToast,
  showFailToast,
} from "vant";
import ArtistRecruitmentApi from "@/apis/artist-recruitment";
import ApiLogin from "@/apis/login";
import WorksPicker from "./components/WorksPicker.vue";
import { REQUEST_OK } from "@/constant/network";

const router = useRouter();
const userInfo = ref({});

const selectedWorks = ref([]);
const feeAmount = ref("");
const submitting = ref(false);
const showWorksPicker = ref(false);
const worksList = ref([]);

const canSubmit = computed(() => {
  return (
    selectedWorks.value.length === 4 &&
    feeAmount.value &&
    parseInt(feeAmount.value) > 0
  );
});

const handleFeeInput = (e) => {
  // 只允许输入正整数
  let value = e.target.value.replace(/[^0-9]/g, "");
  if (value && parseInt(value) <= 0) {
    value = "";
  }
  feeAmount.value = value;
};

const handleSelectWorks = async () => {
  if (worksList.value.length === 0) {
    await fetchWorks();
  }
  showWorksPicker.value = true;
};

const handleWorksConfirm = (works) => {
  selectedWorks.value = works;
  showWorksPicker.value = false;
};

const fetchWorks = async () => {
  try {
    const res = await ArtistRecruitmentApi.getRecentWorks({
      // creatorId: userInfo.value?.userId,
      limit: 20,
      offset: 0,
    });
    console.log("res!!", res);
    if (res?.data?.list?.length > 0) {
      // 转换数据格式
      worksList.value = (res.data.list || []).map((token) => ({
        id: token.id,
        name: token.name,
        cover: token.covers?.[0]?.url || token.imageUrl,
      }));
    } else {
      showToast("获取作品列表失败");
    }
  } catch (error) {
    console.error("获取作品列表失败", error);
  }
};

const handleSubmit = async () => {
  if (!canSubmit.value) {
    if (selectedWorks.value.length !== 4) {
      showToast("请选择4个作品作为例图");
      return;
    }
    if (!feeAmount.value || parseInt(feeAmount.value) <= 0) {
      showToast("请输入正确的稿费金额");
      return;
    }
    return;
  }

  submitting.value = true;
  try {
    const res = await ArtistRecruitmentApi.submitRecruitment({
      tokenIds: selectedWorks.value.map((w) => w.id),
      price: parseInt(feeAmount.value),
    });

    if (res.code === REQUEST_OK) {
      // showSuccessToast("报名成功");
      router.replace({ name: "artist-recruitment-success" });
    } else {
      showFailToast(res.message || "报名失败");
    }
  } catch (error) {
    showFailToast("报名失败，请稍后重试");
  } finally {
    submitting.value = false;
  }
};

// 通过接口获取用户信息
const fetchUserInfo = async () => {
  try {
    const res = await ApiLogin.getUserInfo({});
    if (res.code === REQUEST_OK && res.data?.account) {
      userInfo.value = res.data.account;
    }
  } catch (error) {
    console.error("获取用户信息失败", error);
  }
};

onMounted(async () => {
  await fetchUserInfo();
  fetchWorks();
});
</script>

<style lang="scss" scoped>
.recruitment {
  min-height: 100vh;
  background-color: #fff;
  padding-bottom: 200rpx;

  &-section {
    .section-title {
      font-size: 28rpx;
      font-weight: 600;
      color: #000;
      line-height: 40rpx;
      margin-bottom: 16rpx;
      padding: 24rpx 24rpx 0 24rpx;
    }

    .section-content {
      background-color: #fff;
      border-radius: 16rpx;
      padding: 0 24rpx 24rpx 24rpx;
    }

    .rules-list {
      margin: 0;
      padding-left: 0;
      font-size: 26rpx;
      color: #333;
      line-height: 40rpx;
      list-style: none;
      counter-reset: item;

      li {
        margin-bottom: 16rpx;
        display: flex;

        &::before {
          counter-increment: item;
          content: counter(item) ".";
          flex-shrink: 0;
          margin-right: 8rpx;
        }

        &:last-child {
          margin-bottom: 0;
        }
      }
    }
  }

  &-form {
    padding: 0 30rpx;

    .user-info {
      display: flex;
      align-items: center;
      gap: 8rpx;
      margin-bottom: 32rpx;

      .user-avatar {
        width: 64rpx;
        height: 64rpx;
        border-radius: 50%;
        object-fit: cover;
      }

      .user-name {
        font-size: 32rpx;
        font-weight: 500;
        color: #000;
      }
    }

    .form-group {
      margin-bottom: 32rpx;

      .form-label {
        font-size: 28rpx;
        font-weight: 500;
        color: #000;
        line-height: 44rpx;
        margin-bottom: 16rpx;
      }

      .upload-area {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 192rpx;
        border: 2rpx dashed rgba(0, 0, 0, 0.08);
        border-radius: 16rpx;
        cursor: pointer;

        .upload-icon {
          font-size: 48rpx;
          color: #000;
        }

        .upload-text {
          margin-top: 4rpx;
          font-size: 28rpx;
          color: rgba(0, 0, 0, 0.4);
        }
      }

      .selected-works {
        display: flex;
        gap: 16rpx;
        flex-wrap: wrap;

        .work-item {
          width: 160rpx;
          height: 160rpx;
          border-radius: 8rpx;
          overflow: hidden;

          img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
        }
      }

      .input-wrapper {
        .fee-input {
          width: 100%;
          height: 96rpx;
          padding: 0 24rpx;
          border: 2rpx solid rgba(0, 0, 0, 0.08);
          border-radius: 16rpx;
          font-size: 32rpx;
          color: #000;
          box-sizing: border-box;

          &::placeholder {
            color: rgba(0, 0, 0, 0.4);
          }

          &:focus {
            border-color: var(--primary-purple, #7247dc);
            outline: none;
          }
        }
      }
    }
  }

  &-footer {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 16rpx 20rpx;
    padding-bottom: calc(16rpx + env(safe-area-inset-bottom));
    background-color: #fff;

    .submit-btn {
      height: 96rpx;
      background: linear-gradient(180deg, #2b1ac0 0%, #7247dc 100%);
      border-radius: 12rpx;
      border: none;
      font-size: 32rpx;
      font-weight: 500;
      color: #fff;

      &.disabled {
        background: linear-gradient(
          180deg,
          rgba(43, 26, 192, 0.3) 0%,
          rgba(114, 71, 220, 0.3) 100%
        );
      }
    }
  }
}
</style>
